import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HorizontalLoader } from 'Components'
import { handleError } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import backArrow from '../../../../../Assets/Icon/Users/back-arrow-detailed.svg'
import breadcrumbArrow from '../../../../../Assets/Icon/Referrals/breadcrumb-arrow.svg'
import {
  StationAttendantDetails,
  StationAttendantReferral,
  stationAttendantsBXService,
} from 'BoostXpressAdmin/Services/stationAttendantsBXService'
import './AttendantDetails.scss'

const REFERRALS_LIMIT = 10

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatCurrency = (value?: number | null, symbol = '₦') =>
  `${symbol}${Number(value ?? 0).toLocaleString()}`

const fullName = (firstName?: string | null, lastName?: string | null) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim() || '-'

const AttendantDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [attendant, setAttendant] = useState<StationAttendantDetails | null>(null)
  const [referrals, setReferrals] = useState<StationAttendantReferral[]>([])
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false)
  const [referralsPage, setReferralsPage] = useState(1)
  const [referralsTotal, setReferralsTotal] = useState(0)

  const loadAttendant = async () => {
    if (!id) return

    try {
      setIsLoadingDetails(true)
      const res = await stationAttendantsBXService.show(id)
      setAttendant((res.data?.data ?? null) as StationAttendantDetails | null)
    } catch (error) {
      setAttendant(null)
      handleError(error as any)
    } finally {
      setIsLoadingDetails(false)
    }
  }

  const loadReferrals = async (page: number) => {
    if (!id) return

    try {
      setIsLoadingReferrals(true)
      const res = await stationAttendantsBXService.referrals(id, { page, limit: REFERRALS_LIMIT })
      const responseData = res.data
      const nextReferrals = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.data?.dataset)
          ? responseData.data.dataset
          : []
      const pagination = responseData?.meta?.pagination ?? responseData?.data?.meta?.pagination

      setReferrals(nextReferrals)
      setReferralsTotal(pagination?.total ?? responseData?.meta?.total ?? nextReferrals.length)
    } catch (error) {
      setReferrals([])
      setReferralsTotal(0)
      handleError(error as any)
    } finally {
      setIsLoadingReferrals(false)
    }
  }

  useEffect(() => {
    loadAttendant()
  }, [id])

  useEffect(() => {
    loadReferrals(referralsPage)
  }, [id, referralsPage])

  const referralTotalPages = useMemo(
    () => Math.max(1, Math.ceil(referralsTotal / REFERRALS_LIMIT)),
    [referralsTotal],
  )

  const stationLabel = attendant?.fillingStation
    ? [
      attendant.fillingStation.businessName,
      attendant.fillingStation.branchName,
      attendant.fillingStation.address,
      attendant.fillingStation.city,
      attendant.fillingStation.state,
    ]
      .filter(Boolean)
      .join(', ')
    : '-'

  return (
    <div className='bx-attendant-details'>
      <ToastContainer />

      <div className='bx-attendant-details__breadcrumb'>
        <button
          type='button'
          className='bx-attendant-details__back border-0'
          onClick={() => navigate(-1)}
        >
          <img src={backArrow} alt='Back' />
        </button>

        <div className='bx-attendant-details__crumbs'>
          <button
            type='button'
            className='bx-attendant-details__crumb-button'
            onClick={() => navigate('/boostxpress/attendants')}
          >
            Attendants
          </button>
          <img src={breadcrumbArrow} alt='' />
          <span className='bx-attendant-details__crumb-current'>Attendant Details</span>
        </div>
      </div>

      <div className='bx-attendant-details__heading'>
        <h2>
          {isLoadingDetails ? 'Loading...' : fullName(attendant?.firstName, attendant?.lastName)}
        </h2>
        <span
          className={`bx-attendant-details__status ${attendant?.disabledAt
            ? 'bx-attendant-details__status--inactive'
            : 'bx-attendant-details__status--active'
            }`}
        >
          {attendant?.disabledAt ? 'Inactive' : 'Active'}
        </span>
        <span className='bx-attendant-details__status bx-attendant-details__status--active ms-2 text-capitalize'>
          {attendant?.role || 'Attendant'}
        </span>
      </div>

      <div className='row g-3'>
        {[
          {
            label: 'Total Earned',
            value: formatCurrency(attendant?.totalEarnedMajor, attendant?.currencySymbol || '₦'),
          },
          {
            label: 'Total Withdrawn',
            value: formatCurrency(attendant?.totalWithdrawnMajor, attendant?.currencySymbol || '₦'),
          },
          {
            label: 'Available Balance',
            value: formatCurrency(attendant?.availableMajor, attendant?.currencySymbol || '₦'),
          },
          {
            label: 'Pending Withdrawal',
            value: formatCurrency(
              attendant?.pendingWithdrawalMajor,
              attendant?.currencySymbol || '₦',
            ),
          },
        ].map((item) => (
          <div key={item.label} className='col-12 col-md-6 col-xl-3'>
            <div className='bx-attendant-details__metric-card'>
              <div className='bx-attendant-details__metric-label'>{item.label}</div>
              <div className='bx-attendant-details__metric-value'>
                {isLoadingDetails ? 'Loading...' : item.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isLoadingDetails ? (
        <div className='bx-attendant-details__loader'>
          <HorizontalLoader />
        </div>
      ) : (
        <div className='row g-4'>
          <div className='col-12 col-xl-6'>
            <section className='bx-attendant-details__section'>
              <h3 className='bx-attendant-details__section-title'>Profile Information</h3>
              <div className='bx-attendant-details__card'>
                <div className='bx-attendant-details__row'>
                  <span>First Name</span>
                  <strong>{attendant?.firstName || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Last Name</span>
                  <strong>{attendant?.lastName || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Email</span>
                  <strong>{attendant?.email || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Phone</span>
                  <strong>{attendant?.phone || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Country</span>
                  <strong>{attendant?.country || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Referral Code</span>
                  <strong>{attendant?.referralCode || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Password Updated</span>
                  <strong>{attendant?.isPasswordUpdated ? 'Yes' : 'No'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Email Verified At</span>
                  <strong>{formatDateTime(attendant?.emailVerifiedAt)}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Last Login</span>
                  <strong>{formatDateTime(attendant?.lastLoginAt)}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Created At</span>
                  <strong>{formatDateTime(attendant?.createdAt)}</strong>
                </div>
              </div>
            </section>
          </div>

          <div className='col-12 col-xl-6'>
            <section className='bx-attendant-details__section'>
              <h3 className='bx-attendant-details__section-title'>Station & Bank Information</h3>
              <div className='bx-attendant-details__card'>
                <div className='bx-attendant-details__row'>
                  <span>Filling Station</span>
                  <strong>{stationLabel}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Total Referrals</span>
                  <strong>{attendant?.totalReferrals ?? 0}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Completed Referrals</span>
                  <strong>{attendant?.completedReferrals ?? 0}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Bank Name</span>
                  <strong>{attendant?.bankInfo?.bankName || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Bank Code</span>
                  <strong>{attendant?.bankInfo?.bankCode || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Account Number</span>
                  <strong>{attendant?.bankInfo?.bankAccountNumber || '-'}</strong>
                </div>
                <div className='bx-attendant-details__row'>
                  <span>Account Name</span>
                  <strong>{attendant?.bankInfo?.bankAccountName || '-'}</strong>
                </div>
              </div>
            </section>
          </div>

          <div className='col-12'>
            <section className='bx-attendant-details__section'>
              <div className='bx-attendant-details__section-head'>
                <h3 className='bx-attendant-details__section-title'>Referral History</h3>
                <span className='bx-attendant-details__section-meta'>
                  {referralsTotal} total referral{referralsTotal === 1 ? '' : 's'}
                </span>
              </div>

              <div className='bx-attendant-details__table-card'>
                <div className='table-responsive'>
                  <table className='table bx-attendant-details__table align-middle mb-0'>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Status</th>
                        <th>Referrer</th>
                        <th>Referred</th>
                        <th>Referrer Reward</th>
                        <th>Referred Reward</th>
                        <th>Completed At</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingReferrals ? (
                        <tr>
                          <td colSpan={8} className='text-center py-4'>
                            Loading...
                          </td>
                        </tr>
                      ) : referrals.length ? (
                        referrals.map((referral, index) => (
                          <tr key={referral.resourceId}>
                            <td>{(referralsPage - 1) * REFERRALS_LIMIT + index + 1}</td>
                            <td>
                              <span className='bx-attendant-details__pill'>{referral.status}</span>
                            </td>
                            <td>
                              <div className='bx-attendant-details__person'>
                                <strong>
                                  {fullName(
                                    referral.referrer?.firstName,
                                    referral.referrer?.lastName,
                                  )}
                                </strong>
                                <span>
                                  {referral.referrer?.email || referral.referrer?.phone || '-'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className='bx-attendant-details__person'>
                                <strong>
                                  {fullName(
                                    referral.referred?.firstName,
                                    referral.referred?.lastName,
                                  )}
                                </strong>
                                <span>
                                  {referral.referred?.email || referral.referred?.phone || '-'}
                                </span>
                              </div>
                            </td>
                            <td>
                              {formatCurrency(
                                (referral.referrerRewardMinor ?? 0) / 100,
                                attendant?.currencySymbol || '₦',
                              )}
                            </td>
                            <td>
                              {formatCurrency(
                                (referral.referredRewardMinor ?? 0) / 100,
                                attendant?.currencySymbol || '₦',
                              )}
                            </td>
                            <td>{formatDateTime(referral.completedAt)}</td>
                            <td>{formatDateTime(referral.createdAt)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className='text-center py-4'>
                            No referral records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className='bx-attendant-details__pagination'>
                  <button
                    type='button'
                    className='bx-pagination-btn'
                    disabled={referralsPage <= 1 || isLoadingReferrals}
                    onClick={() => setReferralsPage((value) => Math.max(1, value - 1))}
                  >
                    ← Previous
                  </button>

                  <div className='bx-pagination-pages'>
                    <span className='bx-pagination-page bx-pagination-page--active'>
                      {referralsPage}
                    </span>
                    <span className='bx-pagination-ellipsis'>/</span>
                    <span className='bx-pagination-page'>{referralTotalPages}</span>
                  </div>

                  <button
                    type='button'
                    className='bx-pagination-btn'
                    disabled={referralsPage >= referralTotalPages || isLoadingReferrals}
                    onClick={() =>
                      setReferralsPage((value) => Math.min(referralTotalPages, value + 1))
                    }
                  >
                    Next →
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  )
}

export default AttendantDetails
